import { useSectionOpenPref } from './hooks.js';
import type { Props } from './types.js';
import slugify from '@sindresorhus/slugify';
import * as Collapsible from '@studio/design-system/collapsible';
import { upperFirst } from 'lodash-es';
import type { ReactElement } from 'react';
import { twMerge } from 'cn';
import { Sel, STagNew } from '#~/stag/tag.js';

/** `<Section>` component. */
export const Section = ({
  children,
  className,
  defaultOpen,
  label,
  slug,
  variant = 'sidebar',
  ...props
}: Props): ReactElement => {
  const [open, setOpen] = useSectionOpenPref(
    `room.controls.${slugify(slug)}.open`,
    defaultOpen ?? true
  );

  if (variant === 'inline') {
    return (
      <div className='pt-32'>
        <div>{children}</div>
      </div>
    );
  }

  if (variant === 'tray') {
    return (
      <div className={twMerge('min-h-[60px] p-12', className)} {...props}>
        <div className='my-4 px-4 py-8'>{label ?? upperFirst(slug)}</div>
        <div className='m-4 pb-8'>{children}</div>
      </div>
    );
  }

  /*
   * Be careful messing with this. The `padding` and `margin` styles have
   * been specifically chosen to keep the focus ring from being clipped.
   */
  return (
    <div className={twMerge('min-h-[60px] border-b border-base-100 p-12', className)} {...props}>
      <Collapsible.Root onChange={setOpen} open={open}>
        {/* Per playwright-stag-selectors-only (ai.knowledge/platform):
            the section trigger needs a stable selector so the IR-039
            test can locate the Move section trigger without depending
            on the visible label text. Keyed on `slug` so every section
            instance gets a unique stag. */}
        <Collapsible.Trigger
          className='my-4 px-4 py-8'
          {...STagNew(Sel.MainRoomSection, 'trigger', slug)}
        >
          {label ?? upperFirst(slug)}
        </Collapsible.Trigger>
        <Collapsible.Content>
          <div className='m-4 pb-8'>{children}</div>
        </Collapsible.Content>
      </Collapsible.Root>
    </div>
  );
};
